import { TriangleAlert } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { Button } from '@icebreaker/ui';
import { Field, Textarea } from '@icebreaker/ui';
import { useDialogFocus } from '@icebreaker/ui';
import { confirmToneVariant, dialogStyles, type ConfirmTone } from '@icebreaker/ui';

const MIN_REASON_LENGTH = 20;

export interface ReasonDialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly body?: ReactNode;
  readonly confirmLabel: string;
  /** Onay düğmesinin türü; `ConfirmDialog.tone` ile aynı sözleşme (düğme standardı). */
  readonly tone: ConfirmTone;
  readonly loading?: boolean;
  readonly onConfirm: (reason: string) => void;
  readonly onCancel: () => void;
}

/**
 * ZORUNLU gerekçeli onay penceresi (Story 11.2): yıkıcı eylem gerekçesiz
 * gönderilemez; gerekçe denetim kaydına yazılır. Escape kapatır, odak güvenli
 * eyleme (vazgeç) gider.
 */
export function ReasonDialog({
  open,
  title,
  body,
  confirmLabel,
  tone,
  loading = false,
  onConfirm,
  onCancel,
}: ReasonDialogProps) {
  const t = useTranslation();
  const titleId = useId();
  const fieldId = useId();
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);

  /* Esc, odak tuzağı ve kapanışta odağın geri dönmesi tek kaynaktan gelir. */
  useDialogFocus({ open, panelRef, onEscape: onCancel, lockScroll: true });

  useEffect(() => {
    if (open) return;
    setReason('');
    setTouched(false);
  }, [open]);

  if (!open) return null;

  const trimmed = reason.trim();
  const tooShort = trimmed.length < MIN_REASON_LENGTH;

  return (
    <div className={dialogStyles.backdrop} onClick={onCancel}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={dialogStyles.panel}
        onClick={(event) => event.stopPropagation()}
      >
        {tone !== 'positive' ? (
          <span className={dialogStyles.mark} aria-hidden="true">
            <TriangleAlert size={19} />
          </span>
        ) : null}
        <p id={titleId} className={dialogStyles.title}>
          {title}
        </p>
        {body ? <p className={dialogStyles.body}>{body}</p> : null}
        <Field
          id={fieldId}
          label={t.t('admin.users.reasonLabel')}
          error={touched && tooShort ? t.t('admin.users.reasonTooShort') : undefined}
        >
          <Textarea
            id={fieldId}
            rows={3}
            value={reason}
            placeholder={t.t('admin.users.reasonPlaceholder')}
            invalid={touched && tooShort}
            onChange={(event) => setReason(event.target.value)}
            onBlur={() => setTouched(true)}
          />
        </Field>
        <div className={dialogStyles.actions}>
          <Button autoFocus variant="ghost" onClick={onCancel}>
            {t.t('common.cancel')}
          </Button>
          <Button
            variant={confirmToneVariant(tone)}
            loading={loading}
            loadingLabel={t.t('common.saving')}
            disabled={tooShort}
            onClick={() => {
              setTouched(true);
              if (!tooShort) onConfirm(trimmed);
            }}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
